import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { loadImageAsDataUrl } from './menusConvivesTechnicalSheetPdfGenerator';

export interface TrackingGuest {
    name: string;
    email: string;
    table_number?: string | null;
    invited_at?: string | null;
    responded_at?: string | null;
    checked_in_at?: string | null;
    restrictions: string[];
    allergies: string[];
    has_medical_note?: boolean;
}

interface TrackingPdfData {
    eventLabel: string;
    eventDate?: string;
    total: number;
    invited: number;
    responded: number;
    checkedIn: number;
    responsesClosedAt?: string | null;
    productionLockedAt?: string | null;
    guests: TrackingGuest[];
    issuerName?: string;
    logoUrl?: string | null;
    brandColor?: string;
}

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#6d28d9').replace('#', '');
    return [
        parseInt(clean.substring(0, 2), 16) || 0,
        parseInt(clean.substring(2, 4), 16) || 0,
        parseInt(clean.substring(4, 6), 16) || 0,
    ];
};

const dt = (v?: string | null) => (v ? format(new Date(v), 'dd/MM/yy HH:mm') : '—');

/**
 * Journal de collecte — troisième document de la clôture définitive.
 *
 * La fiche cuisine dit ce qu'il faut préparer, le récap client ce que chacun mange ; celui-ci dit ce qui
 * s'est passé : qui a été sollicité, quand, quand il a répondu, et ce qu'il a déclaré. C'est la pièce qui
 * permet de justifier après coup qu'un convive a bien été invité à signaler ses allergies — la seule qui
 * garde une trace des dates une fois l'événement clos. Demande d'Emeni (2026-08-23).
 *
 * Format paysage : neuf colonnes de dates et de déclarations ne tiennent pas en portrait.
 */
export const generateTrackingPdf = async (data: TrackingPdfData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = 297;
        const marginX = 12;
        const contentW = pageW - marginX * 2;

        const primary = hexToRgb(data.brandColor || '#6d28d9');
        const ink: [number, number, number] = [34, 25, 51];
        const faint: [number, number, number] = [138, 131, 160];
        const border: [number, number, number] = [228, 223, 242];

        let y = 16;

        // En-tête sobre : logo, nom de l'établissement, titre du document.
        if (data.logoUrl) {
            const logo = await loadImageAsDataUrl(data.logoUrl);
            if (logo) {
                // Le canvas de loadImageAsDataUrl produit toujours du PNG, quel que soit le format d'origine.
                const ratio = logo.width / logo.height || 1;
                const h = Math.min(11, 22 / ratio);
                try { doc.addImage(logo.dataUrl, 'PNG', marginX, 8, h * ratio, h); } catch (e) { /* logo ignoré */ }
            }
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(faint[0], faint[1], faint[2]);
        doc.text((data.issuerName || '').toUpperCase(), marginX, 24);

        doc.setFontSize(14);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text('JOURNAL DE COLLECTE', marginX + 30, 14);
        doc.setFontSize(11);
        doc.text(data.eventLabel || 'Privatisation', marginX + 30, 20);
        if (data.eventDate) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(faint[0], faint[1], faint[2]);
            doc.text(format(new Date(data.eventDate), 'EEEE d MMMM yyyy', { locale: fr }), marginX + 30, 25);
        }
        y = 34;

        // Bandeau de synthèse : les chiffres qui comptent pour justifier la collecte.
        doc.setFillColor(primary[0], primary[1], primary[2]);
        doc.roundedRect(marginX, y - 5, contentW, 9, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(255, 255, 255);
        const stats = [
            `${data.total} convive(s)`,
            `${data.invited} invité(s)`,
            `${data.responded} réponse(s)`,
            `${data.checkedIn} installé(s)`,
            data.responsesClosedAt ? `Réponses closes le ${dt(data.responsesClosedAt)}` : 'Réponses non closes',
            data.productionLockedAt ? `Production figée le ${dt(data.productionLockedAt)}` : 'Production non figée',
        ];
        doc.text(stats.join('     ·     '), marginX + 3, y);
        y += 10;

        const cols: [string, number][] = [
            ['CONVIVE', 46], ['EMAIL', 54], ['TABLE', 14],
            ['INVITÉ LE', 26], ['RÉPONDU LE', 26], ['INSTALLÉ LE', 26],
            ['RESTRICTIONS', 40], ['ALLERGIES', 41],
        ];
        const drawHeader = () => {
            doc.setFillColor(244, 241, 252);
            doc.rect(marginX, y - 4, contentW, 6.5, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(primary[0], primary[1], primary[2]);
            let x = marginX + 2;
            cols.forEach(([label, w]) => { doc.text(label, x, y); x += w; });
            y += 6.5;
        };
        drawHeader();

        doc.setFont('helvetica', 'normal');
        data.guests.forEach((g, idx) => {
            if (y > 190) { doc.addPage(); y = 16; drawHeader(); }
            if (idx % 2 === 0) {
                doc.setFillColor(250, 248, 253);
                doc.rect(marginX, y - 4, contentW, 6, 'F');
            }
            const values = [
                g.name + (g.has_medical_note ? ' *' : ''),
                g.email,
                g.table_number || '—',
                dt(g.invited_at),
                dt(g.responded_at),
                dt(g.checked_in_at),
                g.restrictions.join(', ') || '—',
                g.allergies.join(', ') || '—',
            ];
            let x = marginX + 2;
            doc.setFontSize(7);
            values.forEach((v, i) => {
                const w = cols[i][1];
                // Un convive sans réponse est le cas qui intéresse : il ressort en rouge.
                const noAnswer = i === 4 && !g.responded_at;
                doc.setTextColor(noAnswer ? 217 : ink[0], noAnswer ? 45 : ink[1], noAnswer ? 45 : ink[2]);
                doc.text(doc.splitTextToSize(String(v), w - 2)[0] || '', x, y);
                x += w;
            });
            y += 6;
        });

        doc.setDrawColor(border[0], border[1], border[2]);
        doc.line(marginX, y - 2, marginX + contentW, y - 2);
        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(faint[0], faint[1], faint[2]);
        doc.text('* note médicale renseignée par le convive (contenu volontairement non repris dans ce document).', marginX, y);
        doc.text(`Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, pageW / 2, 203, { align: 'center' });

        doc.save(`Journal_collecte_${(data.eventLabel || 'privatisation').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    } catch (e) {
        console.error('Tracking PDF Generation Error:', e);
        alert('Erreur lors de la génération du journal de collecte.');
    }
};
